/**
 * Care-user program assignments: which care programs (service types) a user
 * works in, with effective dating.
 *
 * Shape on users/{uid}:
 *   programAssignments: [{
 *     serviceTypeId, serviceTypeAbbreviation, serviceTypeName,
 *     effectiveDate: 'YYYY-MM-DD', effectiveThruDate: 'YYYY-MM-DD' | null
 *   }]
 *
 * Assignments are managed through the careUsers HTTPS functions (User
 * Management), never by direct client writes.
 */

export const DEFAULT_PROGRAM_ABBREVIATION = 'RPM';

/** Assignment rows on a user doc (defensive: always an array). */
export function getProgramAssignments(user) {
  return Array.isArray(user?.programAssignments) ? user.programAssignments : [];
}

/**
 * Assignments in effect on the given date (defaults to today).
 * @param {object} user - users doc data
 * @param {string} [asOfYmd] - 'YYYY-MM-DD'
 */
export function getActiveProgramAssignments(user, asOfYmd) {
  const asOf = asOfYmd || new Date().toLocaleDateString('en-CA');
  return getProgramAssignments(user).filter((a) => {
    if (!a?.effectiveDate || a.effectiveDate > asOf) return false;
    if (a.effectiveThruDate && a.effectiveThruDate < asOf) return false;
    return true;
  });
}

/**
 * Resolve the program to stamp on a new encounter: the entering user's active
 * assignments intersected with the patient's programs, as of the encounter
 * effective date. Falls back to RPM (today's only live program).
 *
 * @param {object} user - users doc data for the entering user (may be null)
 * @param {object[]} patientPrograms - patientPrograms docs for the patient (may be empty)
 * @param {string} [asOfYmd] - encounter effective date 'YYYY-MM-DD'
 * @returns {{ serviceTypeId: string | null, serviceTypeAbbreviation: string }}
 */
export function resolveEncounterProgram(user, patientPrograms, asOfYmd) {
  const active = getActiveProgramAssignments(user, asOfYmd);
  const programs = Array.isArray(patientPrograms) ? patientPrograms : [];

  const patientTypeIds = new Set(programs.map((p) => p?.serviceTypeId).filter(Boolean));
  const matches = active.filter((a) => patientTypeIds.has(a.serviceTypeId));
  const pool = matches.length ? matches : active;

  if (pool.length === 1) {
    return {
      serviceTypeId: pool[0].serviceTypeId || null,
      serviceTypeAbbreviation: pool[0].serviceTypeAbbreviation || DEFAULT_PROGRAM_ABBREVIATION,
    };
  }
  if (pool.length > 1) {
    // Ambiguous multi-program match: prefer RPM until the encounter form
    // offers an explicit program picker.
    const rpm = pool.find(
      (a) => String(a.serviceTypeAbbreviation || '').toUpperCase() === DEFAULT_PROGRAM_ABBREVIATION,
    );
    const chosen = rpm || pool[0];
    return {
      serviceTypeId: chosen.serviceTypeId || null,
      serviceTypeAbbreviation: chosen.serviceTypeAbbreviation || DEFAULT_PROGRAM_ABBREVIATION,
    };
  }

  // No assignments (e.g. before backfill): fall back to the patient's RPM
  // program when present, else the RPM default.
  const rpmProgram = programs.find(
    (p) => String(p?.serviceTypeAbbreviation || '').toUpperCase() === DEFAULT_PROGRAM_ABBREVIATION,
  );
  return {
    serviceTypeId: rpmProgram?.serviceTypeId || null,
    serviceTypeAbbreviation: DEFAULT_PROGRAM_ABBREVIATION,
  };
}

/** Short display summary, e.g. "RPM (2026-01-01 →)" or "RPM, CCM". */
export function formatProgramAssignmentsSummary(user) {
  const rows = getProgramAssignments(user);
  if (!rows.length) return '—';
  return rows
    .map((a) => {
      const abbr = a.serviceTypeAbbreviation || a.serviceTypeName || '?';
      const thru = a.effectiveThruDate ? ` – ${a.effectiveThruDate}` : ' →';
      return `${abbr} (${a.effectiveDate}${thru})`;
    })
    .join(', ');
}
